import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Plus } from 'lucide-react';
import {
  branchStaff, branchCreateAgent, branchCreateExecutive, branchUpdateStaff,
  branchLockStaff, branchResetStaffPassword, getBranchUser, BranchStaff as StaffRow,
} from '../../services/branchApi';

const TABS = [
  { key: 'deposits', label: 'Deposits' },
  { key: 'withdrawals', label: 'Withdrawals' },
];
const rangeLabel = (min: number, max: number) => `₹${Number(min).toLocaleString()} – ${max ? `₹${Number(max).toLocaleString()}` : '∞'}`;

export default function BranchStaff() {
  const user = getBranchUser();
  const isSM = !!user?.isManager;
  const canAddExecutive = !!user?.canCreateExecutives;   // agents only
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  // dialog: create agent | create executive | edit
  const [dlg, setDlg] = useState<null | { mode: 'agent' | 'executive' | 'edit'; row?: StaffRow }>(null);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => { setLoading(true); try { setRows((await branchStaff()).data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openCreate = (mode: 'agent' | 'executive') => {
    setError('');
    setForm(mode === 'agent'
      ? { username: '', email: '', password: '', rangeMin: 0, rangeMax: 0, canDeposit: true, canWithdraw: true }
      : { username: '', email: '', password: '', tabs: ['deposits'] });
    setDlg({ mode });
  };
  const openEdit = (row: StaffRow) => {
    setError('');
    setForm(row.role === 'agent'
      ? { rangeMin: row.rangeMin, rangeMax: row.rangeMax, canDeposit: row.canDeposit, canWithdraw: row.canWithdraw }
      : { tabs: row.tabs });
    setDlg({ mode: 'edit', row });
  };

  const toggleTab = (key: string) => setForm((f: any) => ({
    ...f, tabs: f.tabs.includes(key) ? f.tabs.filter((t: string) => t !== key) : [...f.tabs, key],
  }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (dlg!.mode === 'agent') await branchCreateAgent(form);
      else if (dlg!.mode === 'executive') await branchCreateExecutive(form);
      else {
        const row = dlg!.row!;
        await branchUpdateStaff(row.id, row.role === 'agent'
          ? { rangeMin: form.rangeMin, rangeMax: form.rangeMax, canDeposit: form.canDeposit, canWithdraw: form.canWithdraw }
          : { tabs: form.tabs });
      }
      setDlg(null); await load();
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  const toggleLock = async (r: StaffRow) => { await branchLockStaff(r.id, r.status !== 'locked'); await load(); };
  const resetPw = async (r: StaffRow) => { const p = window.prompt(`New password for ${r.username}:`); if (p) { await branchResetStaffPassword(r.id, p); alert('Password updated'); } };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{isSM ? 'Agents & Executives' : 'My Executives'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {isSM ? 'Agents approve requests in their amount range; executives help under an agent.' : 'Executives you create share your range and approve on your behalf.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {isSM && <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => openCreate('agent')}>Add Agent</Button>}
          {canAddExecutive && <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => openCreate('executive')}>Add Executive</Button>}
        </Stack>
      </Stack>

      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Username', 'Role', 'Reports to', 'Amount range', 'Deposit', 'Withdraw', 'Status', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: 'text.secondary' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.username}</TableCell>
                <TableCell><Chip size="small" label={r.role} color={r.role === 'agent' ? 'primary' : 'default'} /></TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.parentName || '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.role === 'agent' ? rangeLabel(r.rangeMin, r.rangeMax) : <span style={{ opacity: 0.6 }}>inherited</span>}</TableCell>
                <TableCell>{r.canDeposit ? <Chip size="small" color="success" label="yes" /> : '—'}</TableCell>
                <TableCell>{r.canWithdraw ? <Chip size="small" color="success" label="yes" /> : '—'}</TableCell>
                <TableCell><Chip size="small" color={r.status === 'active' ? 'success' : 'error'} label={r.status} /></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
                    <Button size="small" onClick={() => toggleLock(r)}>{r.status === 'locked' ? 'Unlock' : 'Lock'}</Button>
                    <Button size="small" onClick={() => resetPw(r)}>Reset PW</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No staff yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dlg?.mode === 'agent' ? 'Add Agent' : dlg?.mode === 'executive' ? 'Add Executive' : `Edit ${dlg?.row?.role} · ${dlg?.row?.username}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dlg?.mode !== 'edit' && <>
              <TextField label="Username" size="small" value={form.username || ''} onChange={(e) => setForm((f: any) => ({ ...f, username: e.target.value }))} />
              <TextField label="Email (optional)" size="small" value={form.email || ''} onChange={(e) => setForm((f: any) => ({ ...f, email: e.target.value }))} />
              <TextField label="Password" type="password" size="small" value={form.password || ''} onChange={(e) => setForm((f: any) => ({ ...f, password: e.target.value }))} />
            </>}

            {(dlg?.mode === 'agent' || (dlg?.mode === 'edit' && dlg?.row?.role === 'agent')) && <>
              <Typography variant="caption" color="text.secondary">Requests whose amount falls in this range route to this agent. Max 0 = unlimited.</Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="Min amount" type="number" size="small" value={form.rangeMin ?? 0} onChange={(e) => setForm((f: any) => ({ ...f, rangeMin: Number(e.target.value) }))} />
                <TextField label="Max amount (0 = ∞)" type="number" size="small" value={form.rangeMax ?? 0} onChange={(e) => setForm((f: any) => ({ ...f, rangeMax: Number(e.target.value) }))} />
              </Stack>
              <FormControlLabel control={<Switch checked={!!form.canDeposit} onChange={(e) => setForm((f: any) => ({ ...f, canDeposit: e.target.checked }))} />} label="Can handle deposits" />
              <FormControlLabel control={<Switch checked={!!form.canWithdraw} onChange={(e) => setForm((f: any) => ({ ...f, canWithdraw: e.target.checked }))} />} label="Can handle withdrawals" />
            </>}

            {(dlg?.mode === 'executive' || (dlg?.mode === 'edit' && dlg?.row?.role === 'executive')) && <>
              <Typography variant="caption" color="text.secondary">Select which tabs this executive can work on. They inherit your amount range.</Typography>
              {TABS.map((t) => (
                <FormControlLabel key={t.key} control={<Checkbox checked={form.tabs?.includes(t.key)} onChange={() => toggleTab(t.key)} />} label={t.label} />
              ))}
            </>}

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
